import { normalizeExecutionEnvironmentCapabilities } from "@cmmd-forge/client-runtime";
import { useEffect, useMemo } from "react";

import { useCmmdPlatformAdmin } from "../../cmmd/cmmdPlatformAdmin";
import { useCmmdForgeRuntimeListSnapshot } from "../../cmmd/cmmdForgeRuntimeListResource";
import {
  cmmdDeviceRegistryResource,
  useCmmdDeviceRegistrySnapshot,
} from "../../cmmd/cmmdDeviceRegistryResource";
import { useSavedEnvironmentRuntimeStore } from "../../environments/runtime/savedEnvironmentRuntimeStore";
import { useServerConfig } from "../../rpc/serverState";
import {
  canRecheckDiagnosticsTarget,
  diagnosticsRefreshing,
  providerAuthorityForSelection,
  selectedDiagnosticsRuntimeId,
} from "./useDiagnosticsPanelState.logic";
import {
  activeDiagnosticsTargetKey,
  findDiagnosticsTarget,
  makeDiagnosticsTargetOptions,
  resolveServerDiagnosticsTarget,
} from "./DiagnosticsTargetPicker.logic";
import { useCmmdRuntimeDiagnostics } from "./useCmmdRuntimeDiagnostics";
import { useCloudRuntimeDiagnostics } from "./useCloudRuntimeDiagnostics";
import { useCloudProjectLifecyclePreflight } from "./useCloudProjectLifecyclePreflight";
import { useDiagnosticsRefresh } from "./useDiagnosticsRefresh";
import { useDiagnosticsTabs } from "./useDiagnosticsTabs";
import { useDiagnosticsTopology } from "./useDiagnosticsTopology";
import { useCloudEnvironmentInventory } from "./useCloudEnvironmentInventory";
import { useLocalServerDiagnostics, useRuntimeTraceDiagnostics } from "./useLocalServerDiagnostics";

/**
 * What the page knows about before a target is chosen: the CMMD Environment
 * catalog and the device registry.
 */
function useDiagnosticsSources() {
  const runtimeStates = useSavedEnvironmentRuntimeStore((state) => state.byId);
  const cloudCatalog = useCmmdForgeRuntimeListSnapshot();
  const deviceRegistry = useCmmdDeviceRegistrySnapshot();

  useEffect(() => {
    void cmmdDeviceRegistryResource.refresh().catch(() => undefined);
  }, []);

  return {
    runtimeStates,
    topology: useDiagnosticsTopology({
      cloudCatalog,
      deviceRegistry,
      runtimeStates,
    }),
  } as const;
}

/**
 * Everything the cloud branch reads, assembled where it is read.
 *
 * A sprite needs five sources — the catalog selection, its own trace file,
 * CMMD's lifecycle receipt, the saved history, and the stored provider
 * authority — and folding all five into the page-level hook is what pushed
 * that hook past the complexity gate.
 */
function useCloudDiagnostics(input: {
  readonly active: boolean;
  readonly isPlatformAdmin: boolean;
  readonly topology: ReturnType<typeof useDiagnosticsSources>["topology"];
  readonly runtimeStates: ReturnType<typeof useDiagnosticsSources>["runtimeStates"];
}) {
  const diagnostics = useCloudRuntimeDiagnostics(input.topology.cloudRuntimes);
  // The same trace read the server target gets, taken against the selected
  // sprite's own socket. Only while its tab is showing, because the read walks
  // up to eleven rotated files.
  const traces = useRuntimeTraceDiagnostics(
    input.active ? diagnostics.selectedEnvironmentId : null,
  );
  const inventory = useCloudEnvironmentInventory(
    input.active ? diagnostics.selectedEnvironmentId : null,
    input.isPlatformAdmin,
  );
  const lifecycle = useCloudProjectLifecyclePreflight(
    input.active ? (diagnostics.selectedRuntime?.cmmdProjectId ?? null) : null,
  );

  return {
    diagnostics,
    traces,
    inventory,
    props: {
      diagnostics,
      runtimeErrors: input.topology.cloudRuntimeErrors,
      lifecycleReceipt: lifecycle.receipt,
      lifecycleError: lifecycle.error,
      lifecyclePending: lifecycle.pending,
      providerAuthority: providerAuthorityForSelection({
        selectedEnvironmentId: diagnostics.selectedEnvironmentId,
        runtimeStates: input.runtimeStates,
      }),
      traces: {
        result: traces.result,
        error: traces.error,
        pending: traces.pending,
        unsupportedReason: traces.unsupportedReason,
      },
      inventory: {
        inventory: inventory.inventory,
        error: inventory.error,
        pending: inventory.pending,
        probed: inventory.probed,
      },
      isPlatformAdmin: input.isPlatformAdmin,
    },
  } as const;
}

/**
 * Every read the Diagnostics page performs, and the derivations between them.
 *
 * It lives apart from the component because the two are answerable to
 * different questions: which sources to consult for the selected target, and
 * how to lay the answer out. Keeping them together left one function holding
 * twelve hooks and every cross-source rule between them.
 */
export function useDiagnosticsPanelState() {
  const { runtimeStates, topology } = useDiagnosticsSources();
  const config = useServerConfig();
  const serverLabel = config?.environment.label ?? "This server";

  // On the shared cloud shell the container serving this page belongs to the
  // platform, not to whoever is signed in, so the membership question only
  // needs asking there — every other surface is the user's own machine.
  const sharedControlSurface =
    config === null
      ? false
      : !normalizeExecutionEnvironmentCapabilities(config.environment).runtime.executionPlane;
  const isPlatformAdmin = useCmmdPlatformAdmin(sharedControlSurface);
  const serverTarget = resolveServerDiagnosticsTarget({
    sharedControlSurface,
    isPlatformAdmin,
  });

  const serverTargetVisible = serverTarget !== "hidden";
  const tabs = useDiagnosticsTabs(topology.desktopTargets, serverTargetVisible);
  // Only read while its tab is showing: the inventory shells out to `ps`. The
  // visibility term is belt to the tab correction's braces — it keeps the read
  // from firing in the render before that correction lands, so a user who may
  // not read this container never sends the request that would be refused.
  const localServer = useLocalServerDiagnostics(serverTargetVisible && tabs.activeTab === "server");
  const cloud = useCloudDiagnostics({
    active: tabs.activeTab === "cloud",
    isPlatformAdmin,
    topology,
    runtimeStates,
  });
  const cloudDiagnostics = cloud.diagnostics;

  const runtimeId = selectedDiagnosticsRuntimeId({
    activeTab: tabs.activeTab,
    desktopTargets: topology.desktopTargets,
    selectedCloudRuntimeId: cloudDiagnostics.selectedRuntime?.runtimeId ?? null,
  });

  const canonicalDiagnostics = useCmmdRuntimeDiagnostics(runtimeId);
  const recheck = useDiagnosticsRefresh({
    activeTab: tabs.activeTab,
    canRefreshCurrentRuntime: runtimeId !== null,
    isPlatformAdmin,
    recheckCloudRuntime: cloudDiagnostics.recheck,
    refreshCurrentRuntime: canonicalDiagnostics.refresh,
    refreshLocalServer: localServer.refresh,
    refreshCloudTraces: cloud.traces.refresh,
    refreshCloudInventory: cloud.inventory.refresh,
  });

  const targetGroups = useMemo(
    () =>
      makeDiagnosticsTargetOptions({
        serverLabel,
        serverTarget,
        cloudRuntimes: topology.cloudRuntimes,
        desktopTargets: topology.desktopTargets,
      }),
    [serverLabel, serverTarget, topology.cloudRuntimes, topology.desktopTargets],
  );

  return {
    tabs,
    topology,
    targetGroups,
    activeTarget: findDiagnosticsTarget(
      targetGroups,
      activeDiagnosticsTargetKey({
        activeTab: tabs.activeTab,
        selectedEnvironmentId: cloudDiagnostics.selectedEnvironmentId,
      }),
    ),
    recheck,
    refreshing: diagnosticsRefreshing({
      canonicalFetching: canonicalDiagnostics.fetching,
      cloudChecking: cloudDiagnostics.isChecking,
      localServerPending: localServer.pending,
      cloudTracesPending: cloud.traces.pending,
      cloudInventoryPending: cloud.inventory.pending,
    }),
    canRefresh: canRecheckDiagnosticsTarget({
      activeTab: tabs.activeTab,
      hasSelectedCloudRuntime: cloudDiagnostics.selectedRuntime !== null,
    }),
    canonical: {
      snapshot: canonicalDiagnostics.snapshot,
      error: canonicalDiagnostics.error,
      pending: canonicalDiagnostics.pending,
    },
    cloud: cloud.props,
    server: {
      // Taken off the built option rather than re-derived, so the panel
      // heading and the picker entry cannot end up calling the same machine
      // two different things.
      label: targetGroups.find((group) => group.id === "server")?.options[0]?.label ?? serverLabel,
      diagnostics: localServer,
    },
  } as const;
}
